import { openNotificationEmailAction } from '@/lib/notificationEmailAction';
import {useEffect, useState, type CSSProperties} from 'react';
import { WarningCircle, Bell, Briefcase, Calendar, Check, Checks, CurrencyDollar, ShieldCheck, SpeakerHigh, SpeakerSlash, Users } from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {Button} from '@/components/ui/button';
import {ScrollArea} from '@/components/ui/scroll-area';
import {useNotifications, usePicklistItems, useUserProfile} from '@/hooks/useQueries';
import {useMarkNotificationAsReadMutation, useMarkAllNotificationsAsReadMutation} from '@/hooks/useMutations';
import {
  useHiddenInAppNotificationTypes,
  useSetHiddenInAppNotificationTypes,
} from '@/hooks/useHiddenInAppNotificationTypes';
import {NotificationTypeFilter} from '@/components/NotificationTypeFilter';
import {useToast} from '@/components/ui/use-toast';
import {Notification, NotificationType, PicklistCategory, resolveSkipReasonLabel, stripReasonSuffix, toggleBellFilterGroup} from '@ruta/shared';
import {cn} from '@/lib/utils';
import {ASSISTANT_BRAND} from '@/components/ai-elements/assistant-brand';
import {useNavigate} from 'react-router-dom';
import {parseEntityPath} from '@ruta/shared';
import {resolveEntityRoute} from '@/lib/entityRoutes';
import {
  NOTIFICATION_SOUND_PREFERENCE_EVENT,
  isNotificationSoundEnabled,
  playCashRegister,
  setNotificationSoundEnabled,
} from '@/lib/notificationSounds';

interface NotificationDropdownProps {
  /** Controlled open state — used by the mobile header menu as an anchor. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the bell trigger; pair with controlled open from a parent menu item. */
  hideTrigger?: boolean;
}

export function NotificationDropdown({
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: NotificationDropdownProps = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  // Alert sounds (RUT-3168). Seeded from the stored preference and kept in sync
  // with any other tab or component that flips it.
  const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled);

  useEffect(() => {
    const sync = () => setSoundEnabled(isNotificationSoundEnabled());
    window.addEventListener(NOTIFICATION_SOUND_PREFERENCE_EVENT, sync);
    return () => window.removeEventListener(NOTIFICATION_SOUND_PREFERENCE_EVENT, sync);
  }, []);

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setNotificationSoundEnabled(next);
    setSoundEnabled(next);
    // Turning it on plays the register once, so the admin hears exactly what
    // they've signed up for — and it doubles as the gesture browsers require
    // before audio is allowed to start at all.
    if (next) playCashRegister();
  };

  const hiddenTypes = useHiddenInAppNotificationTypes();
  const setHiddenTypes = useSetHiddenInAppNotificationTypes();
  const { data: userProfile } = useUserProfile();
  const viewerLocale = userProfile?.profile?.languagePreference === 'es' ? 'es' : 'en';
  const { data: skipPicklist } = usePicklistItems(PicklistCategory.SKIP_REASON);

  // React Query hooks
  // Pass `open` so polling pauses while the dropdown is open — a background refetch
  // mid-read rebuilds the list and resets the scroll position.
  // excludeTypes keeps the list and unread badge in agreement with the filter (RUT-3531).
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotifications(25, true, open, hiddenTypes);
  const markAsReadMutation = useMarkNotificationAsReadMutation();
  const markAllAsReadMutation = useMarkAllNotificationsAsReadMutation();

  // Filter out customer_message notifications (shown via message badges instead)
  // and any types this user has hidden — hides immediately while the refetch lands.
  const allNotifications = (data?.pages.flatMap((p) => p?.notifications ?? []) ?? [])
    .filter((n): n is Notification => !!n);
  const notifications = allNotifications.filter(
    (n) => n.type !== NotificationType.CUSTOMER_MESSAGE && !hiddenTypes.includes(n.type),
  );
  const customerMessageUnread = allNotifications.filter(
    (n) => n.type === NotificationType.CUSTOMER_MESSAGE && !n.read
  ).length;
  const unreadCount = Math.max(0, (data?.pages[0]?.unreadCount || 0) - customerMessageUnread);
  const hasNotifications = unreadCount > 0;

  // Callback ref so the effect re-runs once the ScrollArea actually mounts
  // inside Radix's portal — a plain useRef is null on first effect run because
  // DropdownMenuContent's children mount after the parent commits.
  const [scrollAreaEl, setScrollAreaEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open || !hasNextPage || !scrollAreaEl) return;
    const viewport = scrollAreaEl.querySelector(
      '[data-radix-scroll-area-viewport]',
    ) as HTMLElement | null;
    if (!viewport) return;
    const onScroll = () => {
      if (isFetchingNextPage || !hasNextPage) return;
      const remaining = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      if (remaining < 80) fetchNextPage();
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage, notifications.length, scrollAreaEl]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
      toast({
        title: "All notifications marked as read",
      });
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toast({
        title: "Failed to mark notifications as read",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        setMarkingAsRead(notification.id);
        await markAsReadMutation.mutateAsync(notification.id);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        toast({
          title: "Failed to mark notification as read",
          description: "Please try again",
          variant: "destructive",
        });
      } finally {
        setMarkingAsRead(null);
      }
    }

    // Snoozed rate request resurfaced (RUT-2698) — carries no entity actionLink;
    // go to the pending rate-requests work surface (same click-through as the
    // "Rate Requests" pipeline stage).
    if (notification.type === NotificationType.RATE_REQUEST_RESURFACED) {
      navigate('/services?tab=config', {
        state: { clearFilters: true, filters: { status: 'all', rateRequested: true } },
      });
      setOpen(false);
      return;
    }

    // Navigate if there's an action link
    if (openNotificationEmailAction(notification.actionLink)) { setOpen(false); return; }
    if (notification.actionLink) {
      const ref = parseEntityPath(notification.actionLink);
      // Open visits on the customer profile (visit selected) rather than the map view.
      if (ref?.type === 'VISIT' && notification.customerId) {
        navigate(`/customers/${notification.customerId}?visitId=${ref.params.id}`);
        setOpen(false);
        return;
      }
      const route = ref ? resolveEntityRoute(ref) : notification.actionLink;
      if (route) navigate(route);
      setOpen(false);
    }
  };

  const getNotificationIcon = (type: string | undefined) => {
    // Map notification types to appropriate icons
    if (!type) {
      return <Bell className="w-4 h-4" />;
    }

    if (type === NotificationType.SHOP_ADVISOR) {
      return <ShieldCheck className="w-4 h-4" />;
    }

    if (type.includes('rate') || type.includes('pricing')) {
      return <CurrencyDollar className="w-4 h-4" />;
    } else if (type.includes('schedule')) {
      return <Calendar className="w-4 h-4" />;
    } else if (type.includes('payment')) {
      return <CurrencyDollar className="w-4 h-4" />;
    } else if (type.includes('crew')) {
      return <Users className="w-4 h-4" />;
    } else if (type.includes('visit')) {
      return <Briefcase className="w-4 h-4" />;
    } else if (type.includes('system') || type.includes('alert')) {
      return <WarningCircle className="w-4 h-4" />;
    } else {
      return <Bell className="w-4 h-4" />;
    }
  };

  // Round icon tile on the forest-glass panel. The hue sits on the icon (a
  // glyph, never body text), tinted by priority; a read row drops to a faint
  // white so unread rows are the only ones carrying colour.
  const getIconTile = (
    priority: string,
    read: boolean,
  ): { className?: string; style?: CSSProperties } => {
    if (read) return { className: 'bg-white/[0.06] text-white/40' };
    switch (priority) {
      case 'urgent':
        return { className: 'bg-red-500/25 text-red-200' };
      case 'high':
      case 'medium':
        return { className: 'bg-orange-500/25 text-orange-200' };
      default:
        // Sage on the glow — the assistant's own accent, from its brand constants.
        return { style: { background: ASSISTANT_BRAND.glow(0.22), color: ASSISTANT_BRAND.sage } };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {hideTrigger ? (
        // Invisible anchor under the mobile menu button so content pins top-right
        // instead of floating to the viewport center (Radix needs a trigger).
        <DropdownMenuTrigger asChild>
          <span
            aria-hidden
            tabIndex={-1}
            className="pointer-events-none fixed right-12 top-2.5 h-9 w-9 opacity-0"
          />
        </DropdownMenuTrigger>
      ) : (
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative text-white hover:text-white hover:bg-white/10 h-9 w-9 transition-all duration-200"
          >
            <Bell className="h-4 w-4" />
            {hasNotifications && (
              <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-destructive text-destructive-foreground rounded-full text-xs font-medium flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
      )}
      {/* The same forest-glass shell as the Ruta AI panel beside it in the
          header: its background, border, radius, shadow and header gradient
          come from ASSISTANT_BRAND so the two surfaces stay identical. */}
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className={cn(
          'w-[400px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[14px] p-0 text-white',
          'shadow-[0_24px_60px_rgba(0,0,0,0.5),0_0_40px_rgba(72,105,72,0.18)]',
          hideTrigger && 'z-[110]',
        )}
        style={{
          background: ASSISTANT_BRAND.panelBg,
          borderColor: ASSISTANT_BRAND.panelBorder,
          backdropFilter: 'blur(16px) saturate(160%)',
          WebkitBackdropFilter: 'blur(16px) saturate(160%)',
        }}
      >
        <DropdownMenuLabel
          className="relative flex items-center justify-between border-b border-white/[0.06] px-3 pb-3 pt-3.5 font-normal"
          style={{
            background: `linear-gradient(180deg, ${ASSISTANT_BRAND.glow(0.18)} 0%, transparent 100%)`,
          }}
        >
          {/* Filter — left, where the AI panel keeps its history toggle */}
          <NotificationTypeFilter
            onDark
            roleId={userProfile?.profile?.roleId}
            hiddenTypes={hiddenTypes}
            onToggleGroup={(groupId) => setHiddenTypes(toggleBellFilterGroup(hiddenTypes, groupId))}
          />

          {/* Centered title + count, matching the AI header's title block */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
            <div className="flex items-center gap-1.5">
              <Bell size={16} weight="fill" style={{ color: ASSISTANT_BRAND.sage }} className="shrink-0" />
              <span className="text-[14px] font-semibold leading-tight text-white">Notifications</span>
            </div>
            <div className="mt-0.5 text-[10px] leading-tight text-white/50 tabular-nums">
              {hasNotifications
                ? `${unreadCount} unread`
                : 'All caught up'}
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleSound();
              }}
              title={soundEnabled ? 'Mute alert sounds' : 'Unmute alert sounds'}
              aria-label={soundEnabled ? 'Mute alert sounds' : 'Unmute alert sounds'}
              aria-pressed={soundEnabled}
              className={cn(
                'rounded-md p-1.5 transition-colors hover:bg-white/10 hover:text-white',
                soundEnabled ? 'text-white/80' : 'text-white/55',
              )}
            >
              {soundEnabled ? (
                <SpeakerHigh className="h-[20px] w-[20px]" />
              ) : (
                <SpeakerSlash className="h-[20px] w-[20px]" />
              )}
            </button>
            {hasNotifications && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMarkAllAsRead();
                }}
                disabled={markAllAsReadMutation.isPending}
                title="Mark all as read"
                aria-label="Mark all as read"
                className="rounded-md p-1.5 text-white/55 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white/55"
              >
                <Checks className="h-[20px] w-[20px]" />
              </button>
            )}
          </div>
        </DropdownMenuLabel>

        {isLoading ? (
          <div className="p-8 text-center text-white/50">
            <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-2 border-white/40 border-t-transparent" />
            <p className="text-sm">Loading notifications...</p>
          </div>
        ) : notifications.length > 0 ? (
          <ScrollArea ref={setScrollAreaEl} className="h-[400px]">
            {/* Unread rows are the light tiles — a raised white wash with the
                sage dot and a tinted icon. Read rows fall back to the glass. */}
            <div className="flex flex-col gap-1 px-2 py-2">
              {notifications.map((notification: Notification) => {
                const tile = getIconTile(notification.priority, notification.read);
                return (
                <DropdownMenuItem
                  key={notification.id}
                  className={cn(
                    'relative cursor-pointer rounded-lg border px-3 py-2.5 text-white transition-all',
                    'hover:text-white focus:text-white [&_svg]:text-current',
                    notification.read
                      ? 'border-transparent bg-transparent hover:border-white/[0.08] hover:bg-white/[0.06] focus:border-white/[0.08] focus:bg-white/[0.06]'
                      : 'border-white/[0.12] bg-white/[0.08] hover:border-white/[0.18] hover:bg-white/[0.12] focus:border-white/[0.18] focus:bg-white/[0.12]',
                    markingAsRead === notification.id && 'opacity-50',
                  )}
                  disabled={markingAsRead === notification.id}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }}
                >
                  <div className="flex w-full items-start gap-2.5">
                    <div
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                        tile.className,
                      )}
                      style={tile.style}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p
                          className={cn(
                            'text-[12.5px] leading-snug',
                            notification.read ? 'font-medium text-white/60' : 'font-semibold text-white',
                          )}
                        >
                          {notification.title}
                        </p>
                        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[10.5px] tabular-nums text-white/35">
                          {formatTimeAgo(notification.createdAt)}
                          {!notification.read && (
                            <span
                              aria-hidden
                              className="h-1.5 w-1.5 rounded-full"
                              style={{ background: ASSISTANT_BRAND.sage }}
                            />
                          )}
                        </span>
                      </div>
                      <p
                        className={cn(
                          'mt-0.5 line-clamp-2 text-[11.5px] leading-relaxed',
                          notification.read ? 'text-white/40' : 'text-white/70',
                        )}
                      >
                        {notification.skipReason
                          ? stripReasonSuffix(notification.message, notification.skipReason)
                          : notification.message}
                      </p>
                      {/* The skip reason is the payload of a Visit Skipped alert —
                          show it in full on its own line, never clamped (RUT-3557). */}
                      {notification.skipReason && (
                        <p className="mt-0.5 text-[11.5px] leading-relaxed">
                          <span className="text-white/45">Reason: </span>
                          <span className="font-medium text-white/90">
                            {resolveSkipReasonLabel(notification.skipReason, viewerLocale, skipPicklist?.items)}
                          </span>
                        </p>
                      )}
                      {notification.priority === 'urgent' && (
                        <span className="mt-1.5 inline-flex rounded-full border border-red-400/30 bg-red-500/20 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide leading-none text-red-200">
                          Urgent
                        </span>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
                );
              })}
              {isFetchingNextPage && (
                <div className="px-4 py-3 text-center text-[10px] uppercase tracking-[0.14em] text-white/35">
                  Loading more…
                </div>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="px-4 py-10 text-center text-white/35">
            <Check className="mx-auto mb-3 h-8 w-8" style={{ color: ASSISTANT_BRAND.sage }} />
            <p className="mb-1 text-[13px] font-medium text-white/85">All caught up!</p>
            <p className="text-[12px]">No new notifications</p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
